import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./Layout.css";
import { getMenuAccessForRoles, getMenuItems, isOnboardingRoute, isPublicRoute, ROUTES } from "../../config/routeConfig";

import Content from "../content/Content";
import { useLogout } from "../../utils/hooks";
import LogoutTimer from "../login/LogoutTimer";
import CustomPopup from "../../utils/CustomPopup";
import { useUserRole } from "../../context/UserRoleContext";
import FeedbackModal from "../common/FeedbackModal";
import { submitFeedback } from "../../utils/apiClient";

import {
  Footer,
  Header,
  Sidebar,
  SidebarProvider,
  useMenu,
  CustomDialog,
  Button,
} from "@voicecare.ai/ui-library";
import { message } from 'antd';
import { useTheme } from "../../ThemeContext";

const Layout = () => {
  const [isLogoutConfirmationOpen, setIsLogoutConfirmationOpen] =
    useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const { selectedMenu, selectMenu } = useMenu();
  console.log(selectedMenu, 'selectMenuDebugger');
  const { themeMode, currentTheme, toggleTheme } = useTheme();
  const { hasRole } = useUserRole();

  const handleOpenLogoutConfirmation = () => setIsLogoutConfirmationOpen(true);
  const handleCloseLogoutConfirmation = () =>
    setIsLogoutConfirmationOpen(false);

  const handleLogout = async () => {
    await logout();
    handleCloseLogoutConfirmation();
    setTimeout(() => setIsAuthenticated(false), [2000]);
  };

  const handleOpenFeedback = () => setShowFeedbackModal(true);
  const handleCloseFeedback = () => setShowFeedbackModal(false);
  
  const handleSubmitFeedback = async (feedbackText) => {
    try {
      // Get user roles from context to pass to API
      const userRoles = [];
      if (hasRole("admin")) userRoles.push("admin");
      if (hasRole("supervisor")) userRoles.push("supervisor");
      if (userRoles.length === 0) userRoles.push("user");
      
      const userRole = userRoles.join(", ");
      
      // Submit feedback using the API function
      const response = await submitFeedback({ 
        userMessage: feedbackText,
        userRole: userRole
      });
      
      console.log('✅ Feedback submitted successfully:', response);
      setShowFeedbackModal(false);
      
      // Show success message from API response for 4 seconds
      message.success(response.message || 'Feedback submitted successfully!', 10);
      
    } catch (error) {
      console.error('❌ Failed to submit feedback:', error);
      
      // Show error message to user for 4 seconds
      message.error(error.message || 'Failed to submit feedback. Please try again.', 4);
      
      // Close modal even on error (user can retry by clicking feedback button again)
      setShowFeedbackModal(false);
    }
  };

  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem("isAuthenticated") === "true"
  );

  const setIsAuthenticatedAndPersist = (status) => {
    setIsAuthenticated(status);
    localStorage.setItem("isAuthenticated", status);
  };

  const isAuthRoute = isPublicRoute(location.pathname) || location.pathname === "/change-password" || isOnboardingRoute(location.pathname);

  const logout = useLogout();
  const [error, setError] = useState(
    sessionStorage.getItem("Error_401") === "true"
  );

  useEffect(() => {
    const storedError = sessionStorage.getItem("Error_401") === "true";
    setError(storedError);
  }, []); // This effect only needs to run once on mount

  const handleClose = () => {
    logout();
    setIsAuthenticated(false);
    setError(false);
    sessionStorage.setItem("Error_401", "false");
  };

  // Get current customer ID from session storage
  const customerId = sessionStorage.getItem('customerId');
  
  // Get menu items and role access from centralized config
  // Pass customerId to filter API Documentation access
  const customeruiMenus = getMenuItems(customerId);
  const roleMenuAccess = getMenuAccessForRoles();

  // Get filtered menus based on user roles
  const getFilteredMenus = () => {
    let accessibleMenus = new Set();

    // Add menus based on user roles
    if (hasRole("admin")) {
      roleMenuAccess.admin.forEach(menu => accessibleMenus.add(menu));
    } else if (hasRole("supervisor")) {
      roleMenuAccess.supervisor.forEach(menu => accessibleMenus.add(menu));
    } else {
      roleMenuAccess.user.forEach(menu => accessibleMenus.add(menu));
    }

    // Filter the main menu array based on accessible menus
    return customeruiMenus.filter(menu => accessibleMenus.has(menu.name));
  };

  const filteredMenus = getFilteredMenus();

  useEffect(() => {
    localStorage.removeItem("visibleColumns");
  }, [location, navigate]);

  // Sync menu selection with current route
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Create route-to-menu mapping from ROUTES configuration
    const getRouteToMenuMap = () => {
      const routeToMenuMap = {};
      
      // Build mapping from ROUTES object
      Object.values(ROUTES).forEach(route => {
        if (route.name && route.path) {
          routeToMenuMap[route.path] = route.name;
        }
      });
      
      // Add special case for call-automation-v2 -> Call Automation
      routeToMenuMap['/call-automation-v2'] = 'Call Automation';
      
      return routeToMenuMap;
    };

    const routeToMenuMap = getRouteToMenuMap();

    // Handle routes with parameters (like /call-history/:batchId)
    let menuToSelect = routeToMenuMap[currentPath];
    
    if (!menuToSelect) {
      // Check for routes with parameters by finding matching route patterns
      for (const [routePath, menuName] of Object.entries(routeToMenuMap)) {
        if (routePath.includes(':') || currentPath.startsWith(routePath + '/')) {
          // Handle parameterized routes
          const baseRoute = routePath.split('/')[1]; // Get base route part
          const currentBase = currentPath.split('/')[1];
          
          if (baseRoute === currentBase) {
            menuToSelect = menuName;
            break;
          }
        }
      }
      
      // Specific handling for known parameterized routes
      if (!menuToSelect) {
        if (currentPath.startsWith('/call-history/')) {
          menuToSelect = ROUTES.CALL_HISTORY.name;
        } else if (currentPath.startsWith('/call-automation-v2/')) {
          menuToSelect = 'Call Automation';
        }
      }
    }

    // Only select menu if we found a match and it's not already selected
    if (menuToSelect && selectedMenu !== menuToSelect) {
      console.log(`🔄 Syncing menu selection: ${currentPath} -> ${menuToSelect}`);
      selectMenu(menuToSelect);
    }
  }, [location.pathname, selectMenu, selectedMenu]);

  const isVisible = () => {
    // Check if the current URL is the root
    if (window.location.pathname === "/") {
      sessionStorage.setItem("Error_401", "false");
      return false;
    }
    return sessionStorage.getItem("Error_401") === "true";
  };

  const handleProfileClick = () => {
    selectMenu(ROUTES.USER_PROFILE.name);
    navigate(ROUTES.USER_PROFILE.path);
  };

  const handleBillingClick = () => {
    selectMenu(ROUTES.BILLING.name);
    navigate(ROUTES.BILLING.path);
  };

  // State for userName to make it reactive to session storage changes
  const [userName, setUserName] = useState('');

  // Function to read userName from session storage
  const updateUserNameFromSession = () => {
    try {
      const staffInfo = JSON.parse(sessionStorage.getItem("staff_info"));
      setUserName(staffInfo?.name ?? '');
    } catch (error) {
      console.error('Error reading staff info:', error);
      setUserName('');
    }
  };

  // Read userName on component mount and listen for updates
  useEffect(() => {
    updateUserNameFromSession();

    // Listen for custom event when profile is updated
    const handleProfileUpdate = () => {
      updateUserNameFromSession();
    };

    window.addEventListener('userProfileUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
    };
  }, []);

  // Create feedback button component
  const feedbackButton = (
    <Button
      onClick={handleOpenFeedback}
      style={{
        backgroundColor: '#FF6B47',
        borderColor: '#FF6B47',
        fontSize: '0.625rem', // 10px converted to rem
        fontWeight: '500',
        marginRight: '1.25rem', // 20px converted to rem
        marginBottom: '2rem',
        padding: '0.5rem', // Small square padding
        minWidth: '2rem', // Small square width
        height: '2rem', // Small square height
        borderRadius: '0.25rem', // Small border radius for slight rounding
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: '1000'
      }}
      label="Feedback"
    />
  );

  return (
    <SidebarProvider>
      <div className="layout-root">
        {!isAuthRoute && (
          <Sidebar
            selectMenu={selectMenu}
            themeMode={themeMode}
            handleLogout={handleOpenLogoutConfirmation}
            menuItems={filteredMenus}
            location={location}
            navigate={navigate}
            toggleTheme={toggleTheme}
            userName={userName}
            currentTheme={currentTheme}
            onProfileClick={handleProfileClick}
          />
        )}
        <div className="layout-content">
          {!isAuthRoute && (
            <Header
              themeMode={themeMode}
              currentTheme={currentTheme}
              selectedMenu={selectedMenu}
            />
          )}

          <Content
            isAuthenticated={isAuthenticated}
            setIsAuthenticated={setIsAuthenticatedAndPersist}
            selectMenu={selectMenu}
          />
          {!isAuthRoute && (
            <Footer 
              themeMode={themeMode} 
              currentTheme={currentTheme} 
              feedbackButton={feedbackButton}
            />
          )}
          <div style={{ flexDirection: "row", display: "flex" }}>
            <CustomPopup
              content={
                <div>
                  <button className="close-btn" onClick={() => handleClose()}>
                    x
                  </button>
                  <span>Your Session got expired. Please re-login again.</span>
                </div>
              }
              visible={isVisible()}
              setVisible={setError}
              logout={logout}
              setIsAuthenticated={setIsAuthenticated}
            />
          </div>
          <LogoutTimer
            logout={logout}
            setIsAuthenticated={setIsAuthenticated}
          />
        </div>

        <CustomDialog
          open={isLogoutConfirmationOpen}
          message={`Are you sure you want to logout?`}
          onConfirm={handleLogout}
          onCancel={handleCloseLogoutConfirmation}
          themeMode={themeMode}
          currentTheme={currentTheme}
        />

        {/* Feedback Modal */}
        <FeedbackModal
          visible={showFeedbackModal}
          onClose={handleCloseFeedback}
          onSubmit={handleSubmitFeedback}
        />
      </div>
    </SidebarProvider>
  );
};

export default Layout;